import React, { useState, useRef } from 'react'
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom'
import {
  Box, Container, Paper, TextField, Button, Typography,
  Alert, CircularProgress, InputAdornment, IconButton, Link,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Turnstile } from '@marsidev/react-turnstile'
import { useAuth } from '@/hooks/useAuth'
import { useTranslation } from 'react-i18next'
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator'

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'

export const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation()
  const { forgotPassword } = useAuth()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const turnstileRef = useRef<any>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (!turnstileToken) {
      setError(t('auth.forgotPassword.completeCaptcha'))
      setLoading(false)
      return
    }

    try {
      await forgotPassword(email, turnstileToken)
      setSubmitted(true)
    } catch {
      // Don't reveal if email exists or not
      setSubmitted(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            {t('auth.forgotPassword.title')}
          </Typography>

          {submitted ? (
            <Box textAlign="center">
              <Alert severity="success" sx={{ mb: 3 }}>
                {t('auth.forgotPassword.successMessage')}
              </Alert>
              <Link component={RouterLink} to="/login" variant="body2">
                {t('auth.forgotPassword.backToLogin')}
              </Link>
            </Box>
          ) : (
            <>
              <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
                {t('auth.forgotPassword.subtitle')}
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label={t('auth.fields.email')}
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setTurnstileToken(token)}
                    onError={() => setTurnstileToken(null)}
                    onExpire={() => setTurnstileToken(null)}
                  />
                </Box>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading || !email || !turnstileToken}
                >
                  {loading ? <CircularProgress size={24} /> : t('auth.forgotPassword.submit')}
                </Button>
                <Box textAlign="center">
                  <Link component={RouterLink} to="/login" variant="body2">
                    {t('auth.forgotPassword.backToLogin')}
                  </Link>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  )
}

export const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { resetPassword } = useAuth()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const requirements = [
      password.length >= 8,
      /[A-Z]/.test(password),
      /[a-z]/.test(password),
      /\d/.test(password),
    ]
    if (requirements.filter(Boolean).length < 3) {
      setError(t('auth.activation.weakPassword'))
      return
    }
    if (password !== confirmPassword) {
      setError(t('auth.register.passwordMismatch'))
      return
    }

    setLoading(true)
    try {
      await resetPassword(token!, password, confirmPassword)
      navigate('/login', { state: { message: t('auth.resetPassword.success') } })
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('auth.resetPassword.error')
      const errorMap: Record<string, string> = {
        'Invalid or expired reset link': t('auth.resetPassword.invalidToken'),
        'Reset link has expired': t('auth.resetPassword.expiredToken'),
        'Passwords do not match': t('auth.register.passwordMismatch'),
      }
      setError(errorMap[msg] || msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            {t('auth.resetPassword.title')}
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
            {t('auth.resetPassword.subtitle')}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('auth.fields.password')}
              type={showPassword ? 'text' : 'password'}
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <PasswordStrengthIndicator password={password} />
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('auth.fields.confirmPassword')}
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || !password || !confirmPassword}
            >
              {loading ? <CircularProgress size={24} /> : t('auth.resetPassword.submit')}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  )
}
